import { Dispatch, SetStateAction, useState } from "react";
import Modal from "@/components/shared/modal";
import { reportPost } from "@/lib/api/post";
import { usePathname } from "next/navigation";

// Constants
import { PostReportReason, PostReportMessageMaxLength, PostReportOtherReasonMaxLength } from "@/lib/constants/post";

type ReportModalProps = {
  showReportModal: boolean;
  setShowReportModal: Dispatch<SetStateAction<boolean>>;
  translations:any;
};

const ReportModal = ({ showReportModal, setShowReportModal, translations }: ReportModalProps) => {
  const [reason, setReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const pathname = usePathname();
  // extract just the postId from the pathname
  const postId = pathname.split("/").pop() as string;

  const postReportReasonStr = (reason: PostReportReason) => {
    switch (reason) {
      case PostReportReason.CSAM:
        return translations.csamReport;
      case PostReportReason.NCII:
        return translations.nciiReport;
      case PostReportReason.INCEST:
        return translations.incestReport;
      case PostReportReason.BEASTIALITY:
        return translations.beastialityReport;
      case PostReportReason.DMCA:
        return translations.dmcaReport;
      case PostReportReason.MYPOST:
        return translations.mypostReport;
      case PostReportReason.OTHER:
        return translations.otherReport;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    const res: any = await reportPost(postId, reason as PostReportReason, message, otherReason);
    if (res.error) {
      switch (res.error.status) {
        case 400:
          setErrorMsg(translations.alreadySubmittedReport);
          break;
        case 401:
          setErrorMsg(translations.loginToReport);
          break;
        default:
          setErrorMsg(translations.unexpectedErrorReport);
      }
    } else {
      setShowReportModal(false);
    }
  };

  // Function to handle message input with character limit
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    if (input.length <= PostReportMessageMaxLength) {
      setMessage(input);
    }
  };

  // Function to handle otherReason input with character limit
  const handleOtherReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (input.length <= PostReportOtherReasonMaxLength) {
      setOtherReason(input);
    }
  };

  return (
    <Modal showModal={showReportModal} setShowModal={setShowReportModal}>
      <div className="w-full overflow-hidden shadow-xl sm:border-[0.5px] border-0 border-[#3C3C3C] md:max-w-md md:rounded-2xl">
        <div className="flex flex-col space-y-4 bg-[#202124] px-6 py-6">
          <h2 className="text-xl font-bold text-white">{translations.newReport}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-white">{translations.reportReason}</label>
              <select
                value={reason as string}
                onChange={(e) => setReason(e.target.value as PostReportReason)}
                className="w-full rounded-md border border-[#3C3C3C] bg-[#313338] p-2 text-white focus:border-[#DD0772] focus:ring-[#DD0772]"
                required
              >
                <option value="">{translations.selectReportReason}</option>
                {Object.values(PostReportReason).map((r) => (
                  <option key={r} value={r}>
                    {postReportReasonStr(r)}
                  </option>
                ))}
              </select>
            </div>

            {reason === PostReportReason.OTHER && (
              <div>
                <input
                  type="text"
                  value={otherReason}
                  onChange={handleOtherReasonChange}
                  className="w-full rounded-md border border-[#3C3C3C] bg-[#313338] p-2 text-white focus:border-[#DD0772] focus:ring-[#DD0772]"
                  placeholder="Please specify the reason"
                  maxLength={PostReportOtherReasonMaxLength}
                  required
                />
                <div className="flex justify-end mt-1">
                  {/* Changes character counter color when 90% of the max length is reached */}
                  <span className={`text-xs ${otherReason.length >= PostReportOtherReasonMaxLength*0.9 ? (otherReason.length >= PostReportOtherReasonMaxLength ? "text-red-500" : "text-yellow-500") : "text-gray-400"}`}>
                    {otherReason.length}/{PostReportOtherReasonMaxLength}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block mb-1 text-sm font-medium text-white">
                {translations.submitAdditionalDetails}
              </label>
              <textarea
                value={message}
                onChange={handleMessageChange}
                className="w-full rounded-md border border-[#3C3C3C] bg-[#313338] p-2 text-white focus:border-[#DD0772] focus:ring-[#DD0772]"
                rows={4}
                maxLength={500}
                placeholder="Please provide more details about your report"
              />
              <div className="flex justify-end mt-1">
                {/* Changes character counter color when 90% of the max length is reached */}
                <span className={`text-xs ${message.length >= PostReportMessageMaxLength*0.9 ? (message.length >= PostReportMessageMaxLength ? "text-red-500" : "text-yellow-500") : "text-gray-400"}`}>
                  {message.length}/{PostReportMessageMaxLength}
                </span>
              </div>
            </div>
            {errorMsg && <div className="text-sm text-red-500">{errorMsg}</div>}

            <div className="flex justify-end space-x-2">
              <button
                type="submit"
                disabled={!reason || (reason === PostReportReason.OTHER && !otherReason)}
                className={`rounded-md cursor-pointer ${
                  reason && !(reason === PostReportReason.OTHER && !otherReason)
                    ? "bg-[#0772DD] hover:bg-[#0772DD]"
                    : "cursor-not-allowed bg-zinc-800"
                }`}
              >
                <div className={`px-4 py-2 ${reason && !(reason === PostReportReason.OTHER && !otherReason)
                    ? "text-white"
                    : "text-zinc-600"}`}>{translations.submitReport}</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export const useReportModal = (translations:any) => {
  const [show, setShow] = useState(false);
  return {
    setShowReportModal: setShow,
    ReportModal: () => (
      <ReportModal showReportModal={show} setShowReportModal={setShow} translations={translations}/>
    ),
  };
};
